import {
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { IconButton, Tooltip, useMediaQuery } from '@material-ui/core';
import { alpha, makeStyles } from '@material-ui/core/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import CloseIcon from '@material-ui/icons/Close';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import { ConversationProvider } from './conversation';

const AssistantChat = lazy(() =>
  import('./AssistantChat').then(m => ({ default: m.AssistantChat })),
);

const OPEN_KEY = 'assistant.dock.open';
const WIDTH_KEY = 'assistant.dock.width';
const DEFAULT_WIDTH = 420;
const WIDE_WIDTH = 640;
const MIN_WIDTH = 340;
const maxWidth = () => Math.min(900, Math.round(window.innerWidth * 0.6));
const clamp = (w: number) => Math.max(MIN_WIDTH, Math.min(maxWidth(), w));

const read = (key: string) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};
const write = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Private mode: the dock still works, it just forgets.
  }
};

const ease = 'cubic-bezier(0.16, 1, 0.3, 1)';

const useStyles = makeStyles(theme => ({
  root: { display: 'flex', minHeight: '100vh', width: '100%' },
  app: { flex: 1, minWidth: 0 },
  dock: {
    position: 'sticky',
    top: 0,
    height: '100vh',
    flexShrink: 0,
    display: 'flex',
    borderLeft: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    zIndex: theme.zIndex.drawer,
    animation: `$slide 280ms ${ease}`,
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  },
  overlay: {
    position: 'fixed',
    right: 0,
    top: 0,
    bottom: 0,
    maxWidth: '100vw',
    zIndex: theme.zIndex.modal - 1,
    boxShadow: `-16px 0 40px ${alpha(theme.palette.common.black, 0.16)}`,
  },
  // Phones: the whole visible screen, above Backstage's bottom bar. Top and
  // bottom 0 follow the visible viewport where 100vh would not.
  sheet: {
    width: '100vw !important',
    height: 'auto',
    left: 0,
    borderLeft: 0,
    zIndex: theme.zIndex.snackbar + 1,
    boxShadow: 'none',
    paddingTop: 'env(safe-area-inset-top)',
    animation: `$rise 240ms ${ease}`,
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  },
  '@keyframes rise': {
    from: { transform: 'translateY(16px)', opacity: 0 },
    to: { transform: 'none', opacity: 1 },
  },
  '@keyframes slide': {
    from: { transform: 'translateX(24px)', opacity: 0 },
    to: { transform: 'none', opacity: 1 },
  },
  grip: {
    position: 'absolute',
    left: -4,
    top: 0,
    bottom: 0,
    width: 8,
    cursor: 'col-resize',
    zIndex: 1,
    outline: 'none',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 3,
      top: 0,
      bottom: 0,
      width: 2,
      backgroundColor: 'transparent',
      transition: 'background-color 0.15s',
    },
    '&:hover::after, &:focus-visible::after, &[data-dragging="true"]::after': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  inner: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
  loading: { flex: 1 },
  iconButton: {
    padding: 7,
    borderRadius: 6,
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: alpha(theme.palette.divider, 0.55),
      color: theme.palette.text.primary,
    },
    '& svg': { fontSize: 18 },
    '@media (max-width:600px)': { padding: 10 },
  },
  launcher: {
    position: 'fixed',
    right: 20,
    bottom: 20,
    zIndex: theme.zIndex.speedDial,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    height: 44,
    padding: '0 8px 0 16px',
    border: 0,
    borderRadius: 999,
    font: 'inherit',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    boxShadow: `0 6px 20px ${alpha(theme.palette.common.black, 0.18)}`,
    transition: `transform 0.2s ${ease}, box-shadow 0.2s ${ease}`,
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: `0 10px 28px ${alpha(theme.palette.common.black, 0.22)}`,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 3,
    },
    '& svg': { fontSize: 18 },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  // Phones: a round button above Backstage's 56px bottom bar.
  launcherPhone: {
    right: 16,
    bottom: 'calc(56px + 16px + env(safe-area-inset-bottom))',
    width: 52,
    height: 52,
    padding: 0,
    justifyContent: 'center',
    '& svg': { fontSize: 22 },
  },
  kbd: {
    fontFamily: 'inherit',
    fontSize: 11.5,
    lineHeight: 1,
    padding: '6px 8px',
    borderRadius: 999,
    backgroundColor: alpha(theme.palette.primary.contrastText, 0.16),
  },
}));

const isMac = () =>
  typeof navigator !== 'undefined' &&
  /Mac|iP(hone|ad)/.test(navigator.platform);

/**
 * The app with the assistant docked to its right. On wide screens the dock
 * takes a column beside the page so both stay readable; on narrow screens it
 * overlays the page, and on phones it fills the screen.
 */
export const DockLayout = ({ children }: { children: ReactNode }) => {
  const classes = useStyles();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const docked = useMediaQuery('(min-width:1100px)', { noSsr: true });
  const phone = useMediaQuery('(max-width:600px)', { noSsr: true });
  const [open, setOpen] = useState(() => read(OPEN_KEY) === 'true');
  const [width, setWidth] = useState(() => {
    const saved = Number(read(WIDTH_KEY));
    return saved ? clamp(saved) : DEFAULT_WIDTH;
  });
  const [dragging, setDragging] = useState(false);
  const [focusKey, setFocusKey] = useState(0);
  const dock = useRef<HTMLElement>(null);
  const launcher = useRef<HTMLButtonElement>(null);
  const onFullPage = pathname.startsWith('/assistant');
  const sheetOpen = phone && open && !onFullPage;

  // The page behind a full-screen sheet must not scroll under the finger.
  useEffect(() => {
    if (!sheetOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [sheetOpen]);

  const show = useCallback((next: boolean) => {
    setOpen(next);
    write(OPEN_KEY, String(next));
    if (next) setFocusKey(k => k + 1);
    else window.setTimeout(() => launcher.current?.focus(), 0);
  }, []);

  const resize = useCallback((w: number) => {
    const next = clamp(w);
    setWidth(next);
    write(WIDTH_KEY, String(next));
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && (e.ctrlKey || e.metaKey) && !e.altKey) {
        e.preventDefault();
        if (onFullPage) return;
        const inside = dock.current?.contains(document.activeElement);
        if (open && !inside) setFocusKey(k => k + 1);
        else show(!open);
      } else if (e.key === 'Escape' && open && !onFullPage) {
        if (dock.current?.contains(document.activeElement)) show(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onFullPage, show]);

  const startDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = width;
    setDragging(true);
    const move = (ev: PointerEvent) => resize(startW + (startX - ev.clientX));
    const up = () => {
      setDragging(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.style.removeProperty('user-select');
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const onGripKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft') resize(width + 24);
    else if (e.key === 'ArrowRight') resize(width - 24);
    else return;
    e.preventDefault();
  };

  const wide = width >= WIDE_WIDTH - 1;
  const showDock = open && !onFullPage;
  const shortcut = isMac() ? '⌘ /' : 'Ctrl /';

  const actions = (
    <>
      {!phone && (
        <Tooltip title={wide ? 'Narrow' : 'Widen'}>
          <IconButton
            className={classes.iconButton}
            aria-label={wide ? 'Narrow the assistant' : 'Widen the assistant'}
            onClick={() => resize(wide ? DEFAULT_WIDTH : WIDE_WIDTH)}
          >
            {wide ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      )}
      {!phone && (
        <Tooltip title="Open as a full page">
          <IconButton
            className={classes.iconButton}
            aria-label="Open as a full page"
            onClick={() => {
              show(false);
              navigate('/assistant');
            }}
          >
            <OpenInNewIcon />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={`Close (${shortcut})`}>
        <IconButton
          className={classes.iconButton}
          aria-label="Close the assistant"
          onClick={() => show(false)}
        >
          <CloseIcon />
        </IconButton>
      </Tooltip>
    </>
  );

  return (
    <div className={classes.root}>
      <div className={classes.app}>{children}</div>
      {showDock && (
        <aside
          ref={dock}
          aria-label="Assistant"
          className={`${classes.dock} ${docked ? '' : classes.overlay} ${
            phone ? classes.sheet : ''
          }`}
          style={{ width: phone ? undefined : width }}
        >
          {!phone && (
            // A focusable separator is the ARIA window-splitter pattern;
            // the lint rule treats every separator as static.
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <div
              className={classes.grip}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize the assistant"
              aria-valuenow={width}
              aria-valuemin={MIN_WIDTH}
              aria-valuemax={maxWidth()}
              // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
              tabIndex={0}
              data-dragging={dragging}
              onPointerDown={startDrag}
              onKeyDown={onGripKey}
            />
          )}
          <div className={classes.inner}>
            <Suspense fallback={<div className={classes.loading} />}>
              <AssistantChat
                variant="panel"
                actions={actions}
                focusKey={focusKey}
              />
            </Suspense>
          </div>
        </aside>
      )}
      {!showDock && !onFullPage && (
        <button
          ref={launcher}
          type="button"
          className={`${classes.launcher} ${
            phone ? classes.launcherPhone : ''
          }`}
          onClick={() => show(true)}
          aria-label={phone ? 'Ask the assistant' : undefined}
          aria-keyshortcuts="Control+/ Meta+/"
        >
          <ChatBubbleOutlineIcon aria-hidden />
          {!phone && (
            <>
              Ask the assistant
              <kbd className={classes.kbd}>{shortcut}</kbd>
            </>
          )}
        </button>
      )}
    </div>
  );
};

/** App root wrapper: one conversation shared by the dock and the full page. */
export const AssistantRoot = ({ children }: { children: ReactNode }) => (
  <ConversationProvider>
    <DockLayout>{children}</DockLayout>
  </ConversationProvider>
);
